import { readSSE, type SSEHandler } from './sse.ts';

/**
 * Attach to a headless build's progress stream, and keep attached.
 *
 * A build job outlives any one connection — the server buffers its events and
 * replays them on re-attach — so dropping the pipe must not lose the build.
 * But "reconnect on failure" alone is not enough, and both build surfaces
 * previously shipped the same broken shape: a bare catch that treated EVERY
 * failure as transient and retried every 15s forever. The job being GONE
 * (server restarted, or reaped 30 minutes after it went terminal) answers 404,
 * which is permanent — retrying it left the new-deck wizard spinning "Writing
 * slides…" indefinitely with no error, no give-up, and the retry chips
 * unreachable because `phase` can only leave 'building' via a `done` event that
 * is never coming.
 *
 * So this distinguishes the three endings the callers actually need:
 *
 *  - `done`     — the stream delivered a terminal event. Normal path.
 *  - `gone`     — 404: the job no longer exists server-side. Terminal, no retry.
 *  - `unknown`  — we ran out of attempts on transport errors. Terminal, and the
 *                 caller must say so rather than spin.
 *
 * `stallMs` matters too: the server sends a keepalive every 20s precisely so a
 * half-open connection (proxy dropped it without a FIN) is detectable. Without
 * it a dead socket looks identical to a quiet build.
 *
 * Returns a handle with `detach()` — call it on unmount. The section-build copy
 * had no cleanup at all, so navigating away from a deck left the recursion
 * fetching and calling setState on a dead component for the life of the page.
 */
/** How the attachment ended. `gone` and `unknown` are both terminal, and the
 *  caller must SAY so rather than spin — that distinction is the whole point
 *  of this module. */
export type BuildProgressEnd = 'done' | 'gone' | 'unknown';

export type BuildProgressOptions = {
  jobId: string;
  onEvent: SSEHandler;
  onEnd?: (reason: BuildProgressEnd) => void;
  maxAttempts?: number;
  stallMs?: number;
  /** False once this attachment is stale (the component moved to another job),
   *  so a late reconnect drops rather than writing to a dead surface. */
  isCurrent?: () => boolean;
};

export function attachBuildProgress({
  jobId,
  onEvent,
  onEnd,
  maxAttempts = 8,
  stallMs = 120_000,
  isCurrent = () => true,
}: BuildProgressOptions): { detach: () => void } {
  let detached = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const finish = (reason: BuildProgressEnd) => {
    if (detached) return;
    detached = true;
    onEnd?.(reason);
  };

  const run = async (attempt: number): Promise<void> => {
    if (detached || !isCurrent()) return;
    let sawDone = false;
    try {
      const res = await fetch(`/api/wizard/build-progress/${jobId}`, { credentials: 'same-origin' });
      // 404 = the job is gone, not a blip. Retrying it is what produced the
      // permanent "building…" state; stop and let the caller say what happened.
      if (res.status === 404) return finish('gone');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      sawDone = await readSSE(res, onEvent, { stallMs });
    } catch {
      /* transport error — retried below, up to the attempt budget */
    }
    if (detached || !isCurrent()) return;
    if (sawDone) return finish('done');
    if (attempt + 1 >= maxAttempts) return finish('unknown');
    timer = setTimeout(() => run(attempt + 1), Math.min(15000, 1000 * 2 ** attempt));
  };

  void run(0);

  return {
    detach() {
      detached = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
